'use client';

import * as Dialog from '@radix-ui/react-dialog';
import { useEffect, useRef, useState, type RefObject } from 'react';

import { DoneStep } from '@eliadmin/core/src/components/dlz/auth/password-reset/done-step';
import { EmailStep } from '@eliadmin/core/src/components/dlz/auth/password-reset/email-step';
import { RESET_COPY } from '@eliadmin/core/src/components/dlz/auth/password-reset/password-reset-copy';
import {
  ResetStepper,
  type ResetStepNumber,
} from '@eliadmin/core/src/components/dlz/auth/password-reset/reset-stepper';
import { ResetStep } from '@eliadmin/core/src/components/dlz/auth/password-reset/reset-step';
import { SentStep } from '@eliadmin/core/src/components/dlz/auth/password-reset/sent-step';
import { useResendCooldown } from '@eliadmin/core/src/components/dlz/auth/password-reset/use-resend-cooldown';
import { cn } from '@eliadmin/core/src/utils/style-utils';

export type PasswordResetStep = 'email' | 'sent' | 'reset' | 'done';

export type PasswordResetCardProps = {
  initialStep: PasswordResetStep;
  /** Address of the reset link (`?email=`); prefills step 1 and names the account in steps 2–3. */
  email?: string;
  /** Token of the reset link (`?token=`), needed by step 3. */
  token?: string;
  /** Phone and opening hours of the „Nemáte prístup k e-mailu?“ line; both come from the administration. */
  supportPhone?: string;
  supportHours?: string;
};

const STEP_NUMBER: Record<PasswordResetStep, ResetStepNumber | null> = {
  email: 1,
  sent: 2,
  reset: 3,
  done: null,
};

const CARD_SHADOW = 'shadow-[0_8px_24px_-4px_rgba(15,16,18,0.08),0_2px_4px_rgba(15,16,18,0.06)]';

/*
 * Position and size of the card per step (Figma „Card“ 652 px, „Review form“ 539 px). The cards are
 * centred with auto margins, not a transform, so the open animation cannot fight the centring; the
 * Figma frames centre them at 50 % + 0.5 px, hence the half pixel offset on desktop.
 */
const DESKTOP_CENTERED = 'md:bottom-[-0.5px] md:top-[0.5px] md:m-auto md:h-fit';
const FORM_CARD = cn(
  'inset-x-0 bottom-0 max-h-[calc(100dvh-16px)] rounded-t-[24px] border border-[#E2E4E7] px-4 py-12 data-[state=open]:slide-in-from-bottom',
  'md:inset-x-0 md:max-h-[calc(100dvh-32px)] md:w-[652px] md:rounded-[18px] md:p-12 md:data-[state=open]:slide-in-from-bottom-0 md:data-[state=open]:zoom-in-95',
  DESKTOP_CENTERED,
);
const DONE_CARD = cn(
  'inset-x-4 inset-y-0 m-auto h-fit max-h-[calc(100dvh-32px)] rounded-[18px] px-4 py-6 data-[state=open]:zoom-in-95',
  'md:w-[539px] md:p-6',
  DESKTOP_CENTERED,
);

/** Moves focus to the field or heading the new step marks with `data-autofocus`. */
function FocusStepTarget({
  step,
  container,
}: {
  step: PasswordResetStep;
  container: RefObject<HTMLElement | null>;
}) {
  useEffect(() => {
    container.current?.querySelector<HTMLElement>('[data-autofocus]')?.focus();
  }, [step, container]);

  return null;
}

/**
 * Forgotten-password flow in one dialog – Figma „MOD – Zabudnuté heslo 1/2“ and „Nastavenie nového
 * hesla 3/4“ (60155:20067, 60155:20277, 60158:20811, 60158:21486; mobile 60438:81985 – 60438:82686).
 *
 *   email → sent (e-mail link requested) · reset (link opened, new password) → done
 *
 * Steps 1–3 are a 652 px card on desktop and a bottom sheet on mobile, step 4 a compact centred
 * card. It is one card of the authentication dialog (under its overlay); the exit links go back to
 * the login card, the overlay and Escape close the whole dialog.
 */
export function PasswordResetCard({
  initialStep,
  email,
  token,
  supportPhone,
  supportHours,
}: PasswordResetCardProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState(initialStep);
  const [address, setAddress] = useState(email ?? '');
  const [loggedOutOtherDevices, setLoggedOutOtherDevices] = useState(true);
  const [announcement, setAnnouncement] = useState({ text: '', count: 0 });
  const { remaining, restart } = useResendCooldown(initialStep === 'sent');

  const announce = (text: string) =>
    setAnnouncement(current => ({ text, count: current.count + 1 }));

  const goTo = (next: PasswordResetStep) => {
    const number = STEP_NUMBER[next];
    setStep(next);
    announce(
      number
        ? RESET_COPY.stepper.announce(
            number,
            RESET_COPY.stepper.steps.length,
            RESET_COPY.stepper.steps[number - 1],
          )
        : '',
    );
  };

  const stepNumber = STEP_NUMBER[step];

  return (
    <Dialog.Content
      ref={contentRef}
      onOpenAutoFocus={event => event.preventDefault()}
      className={cn(
        'fixed z-[200] flex flex-col items-center gap-6 overflow-y-auto bg-white focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 motion-reduce:animate-none',
        CARD_SHADOW,
        stepNumber ? cn(FORM_CARD, step === 'reset' && 'px-5 md:px-12') : DONE_CARD,
      )}
    >
      <FocusStepTarget step={step} container={contentRef} />
      <div role="status" aria-live="polite" className="sr-only">
        {announcement.text}
        {/* A repeated message is only read again when the text node changes. */}
        {announcement.count % 2 === 0 ? '' : ' '}
      </div>
      {stepNumber ? <ResetStepper current={stepNumber} /> : null}
      <div
        key={step}
        className="flex w-full flex-col items-center gap-6 duration-200 animate-in fade-in-0 motion-reduce:animate-none"
      >
        {step === 'email' ? (
          <EmailStep
            defaultEmail={address}
            supportPhone={supportPhone}
            supportHours={supportHours}
            onSent={sentTo => {
              setAddress(sentTo);
              restart();
              goTo('sent');
            }}
          />
        ) : null}
        {step === 'sent' ? (
          <SentStep
            email={address}
            secondsLeft={remaining}
            onResent={() => {
              restart();
              announce(RESET_COPY.sent.resent);
            }}
            onChangeEmail={() => goTo('email')}
          />
        ) : null}
        {step === 'reset' ? (
          <ResetStep
            email={email}
            token={token}
            onDone={loggedOut => {
              setLoggedOutOtherDevices(loggedOut);
              goTo('done');
            }}
          />
        ) : null}
        {step === 'done' ? <DoneStep loggedOutOtherDevices={loggedOutOtherDevices} /> : null}
      </div>
    </Dialog.Content>
  );
}
